import { Controller } from '@hotwired/stimulus'

/**
 * Show/hide toggle for a password input rendered by PasswordFieldComponent.
 *
 * The input is the caller's own markup, so it is located by query instead of a target.
 * The toggle is a WAI-ARIA toggle button: its label stays "Show password" and
 * `aria-pressed` reports the state, so screen readers announce "Show password, pressed".
 */
export default class extends Controller<HTMLElement> {
  static targets = ['toggle', 'icon']

  declare readonly toggleTarget: HTMLButtonElement

  declare readonly iconTarget: HTMLElement

  connect(): void {
    const { input } = this
    if (!input) return

    this.toggleTarget.disabled = input.disabled
    this.reveal(false)
  }

  // Masks again before Turbo snapshots the page, so a cached restore never shows the secret.
  disconnect(): void {
    this.reveal(false)
  }

  toggle(): void {
    const { input } = this
    if (!input) return

    this.reveal(input.type === 'password')
  }

  private reveal(revealed: boolean): void {
    const { input } = this
    if (!input) return

    input.type = revealed ? 'text' : 'password'
    this.toggleTarget.setAttribute('aria-pressed', String(revealed))
    this.iconTarget.classList.toggle('fa-eye', !revealed)
    this.iconTarget.classList.toggle('fa-eye-slash', revealed)
  }

  private get input(): HTMLInputElement | null {
    return this.element.querySelector<HTMLInputElement>('input:not([type="hidden"])')
  }
}
